import React, { useCallback } from 'react';
import { Mic } from 'lucide-react';
import { useTranslation } from 'react-i18next';

/**
 * AudioToggle Component
 * Renders a chip to toggle between Whisper transcription and realtime audio modes
 *
 * @param {Object} props
 * @param {boolean} props.disabled - Whether the toggle is disabled
 * @param {Object} props.assistantColors - Color scheme for the UI
 * @param {boolean} props.show - Show the toggle chip
 * @param {boolean} props.realtimeAvailable - Whether realtime mode is available
 * @param {boolean} props.conversationStarted - Whether a conversation has started (hides toggle)
 * @param {Function} props.onConnect - Callback to connect to realtime mode
 * @param {Function} props.onDisconnect - Callback to disconnect from realtime mode
 * @param {boolean} props.isConnected - Whether realtime is currently connected
 * @param {boolean} props.realtimeModeEnabled - Whether realtime mode is enabled (controlled state)
 * @param {Function} props.onModeChange - Callback when mode changes
 */
const AudioToggle = ({
  disabled = false,
  assistantColors,
  show = false,
  realtimeAvailable = false,
  conversationStarted = false,
  onConnect,
  onDisconnect,
  isConnected = false,
  realtimeModeEnabled = false,
  onModeChange,
}) => {
  const { t } = useTranslation();

  // Handle Audio chip toggle
  const handleAudioToggle = useCallback(async () => {
    if (!realtimeModeEnabled) {
      // Enable realtime mode and connect
      await onConnect?.();
      onModeChange?.(true);
    } else {
      // Disable realtime mode and disconnect
      await onDisconnect?.();
    }
  }, [realtimeModeEnabled, onConnect, onDisconnect, onModeChange]);

  // Don't render if conditions aren't met
  if (!show || !realtimeAvailable) {
    return null;
  }

  const isActive = realtimeModeEnabled;
  const isChipDisabled = disabled || realtimeModeEnabled || conversationStarted;
  const primaryColor = assistantColors?.primaryColor || 'black';
  const secondaryColor = assistantColors?.secondaryColor || 'black';

  return (
    <button
      type="button"
      onClick={handleAudioToggle}
      disabled={isChipDisabled}
      className="inline-flex items-center gap-1.5 h-7 px-3 text-xs font-medium rounded-full border transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
      style={{
        borderColor: secondaryColor,
        backgroundColor: isActive ? primaryColor : 'transparent',
        color: isActive ? 'white' : secondaryColor,
      }}
      onMouseEnter={(e) => {
        if (!e.currentTarget.disabled) {
          e.currentTarget.style.backgroundColor = primaryColor;
          e.currentTarget.style.color = 'white';
          e.currentTarget.style.borderColor = primaryColor;
        }
      }}
      onMouseLeave={(e) => {
        if (isActive) {
          e.currentTarget.style.backgroundColor = primaryColor;
          e.currentTarget.style.color = 'white';
          e.currentTarget.style.borderColor = primaryColor;
        } else {
          e.currentTarget.style.backgroundColor = 'transparent';
          e.currentTarget.style.color = secondaryColor;
          e.currentTarget.style.borderColor = secondaryColor;
        }
      }}
    >
      <Mic size={14} />
      {t("realtime")}
    </button>
  );
};

export default AudioToggle;
